import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

@Component({
  selector: 'app-shift-time-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './shift-time-input.component.html',
  styleUrl: './shift-time-input.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShiftTimeInputComponent),
      multi: true,
    },
  ],
})
export class ShiftTimeInputComponent implements ControlValueAccessor {
  private static nextInstanceId = 0;

  @Input() placeholder = 'off';
  @Input() presets: string[] = [
    'off',
    '9:00 AM - 5:00 PM',
    '9:30 AM - 5:30 PM',
    '10:00 AM - 6:00 PM',
    '11:00 AM - 7:00 PM',
    '11:00 AM - 6:30 PM',
    '2:00 PM - 10:00 PM',
    '4:00 PM - 10:00 PM',
  ];

  value = '';
  filterQuery = '';
  isOpen = false;
  disabled = false;
  highlightedIndex = -1;

  readonly listboxId = `shift-time-input-list-${ShiftTimeInputComponent.nextInstanceId++}`;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  get filteredPresets(): string[] {
    const source = this.presets ?? [];
    const query = this.filterQuery.trim().toLowerCase();

    if (!query) {
      return source;
    }

    return source.filter((preset) => preset.toLowerCase().includes(query));
  }

  get activeDescendantId(): string | null {
    if (!this.isOpen || this.highlightedIndex < 0) {
      return null;
    }

    return this.getOptionId(this.highlightedIndex);
  }

  writeValue(value: string | null | undefined): void {
    this.value = value ?? '';
    this.filterQuery = '';
    this.syncHighlightedWithValue();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.isOpen = false;
      this.highlightedIndex = -1;
    }

    this.cdr.markForCheck();
  }

  onInputChange(nextValue: string): void {
    this.value = nextValue;
    this.filterQuery = nextValue;
    this.onChange(this.value);

    if (!this.isOpen && !this.disabled) {
      this.isOpen = true;
    }

    this.syncHighlightedWithValue();
    this.cdr.markForCheck();
  }

  onInputFocus(): void {
    if (this.disabled) {
      return;
    }

    this.filterQuery = '';
    this.isOpen = true;
    this.syncHighlightedWithValue();
    this.cdr.markForCheck();
  }

  onInputBlur(): void {
    this.onTouched();
  }

  onToggleDropdown(event: MouseEvent): void {
    event.stopPropagation();

    if (this.disabled) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.filterQuery = '';
      this.syncHighlightedWithValue();
    } else {
      this.highlightedIndex = -1;
    }

    this.cdr.markForCheck();
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveHighlight(-1);
        break;
      case 'Enter':
        if (this.isOpen && this.highlightedIndex >= 0) {
          event.preventDefault();
          this.selectHighlighted();
        }
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.closeDropdown();
        }
        break;
      default:
        break;
    }
  }

  onOptionMouseDown(event: MouseEvent): void {
    // Keep focus on the input so option click can apply cleanly.
    event.preventDefault();
  }

  onOptionClick(event: MouseEvent, preset: string): void {
    event.stopPropagation();
    this.selectPreset(preset);
  }

  isOptionHighlighted(index: number): boolean {
    return index === this.highlightedIndex;
  }

  isCurrentValueOption(preset: string): boolean {
    return preset.toLowerCase() === this.value.trim().toLowerCase();
  }

  getOptionId(index: number): string {
    return `${this.listboxId}-option-${index}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.closeDropdown();
    }
  }

  private selectPreset(preset: string): void {
    this.value = preset;
    this.onChange(this.value);
    this.onTouched();
    this.closeDropdown();
    this.syncHighlightedWithValue();
    this.cdr.markForCheck();
  }

  private moveHighlight(direction: 1 | -1): void {
    const options = this.filteredPresets;

    if (!options.length) {
      this.highlightedIndex = -1;
      this.isOpen = true;
      this.cdr.markForCheck();
      return;
    }

    if (!this.isOpen) {
      this.isOpen = true;
    }

    if (this.highlightedIndex < 0) {
      this.highlightedIndex = direction > 0 ? 0 : options.length - 1;
    } else {
      this.highlightedIndex =
        (this.highlightedIndex + direction + options.length) % options.length;
    }

    this.cdr.markForCheck();
  }

  private selectHighlighted(): void {
    const options = this.filteredPresets;
    if (this.highlightedIndex < 0 || this.highlightedIndex >= options.length) {
      return;
    }

    this.selectPreset(options[this.highlightedIndex]);
  }

  private closeDropdown(): void {
    this.isOpen = false;
    this.highlightedIndex = -1;
    this.filterQuery = '';
    this.cdr.markForCheck();
  }

  private syncHighlightedWithValue(): void {
    const options = this.filteredPresets;
    if (!options.length) {
      this.highlightedIndex = -1;
      return;
    }

    const selectedIndex = options.findIndex(
      (preset) => preset.toLowerCase() === this.value.trim().toLowerCase(),
    );
    this.highlightedIndex = selectedIndex >= 0 ? selectedIndex : 0;
  }
}
